import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { log } from '../../lib/logger';

interface Props { children: ReactNode; }
interface State { hasError: boolean; error: Error | null; }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    log.error('ErrorBoundary caught:', error, { componentStack: info.componentStack });
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '60px 20px', display: 'flex', justifyContent: 'center' }}>
          <div style={{
            maxWidth: 420, width: '100%', padding: '32px',
            background: palette.redWash, borderRadius: 10,
            borderLeft: `4px solid ${palette.red}`, textAlign: 'center',
          }}>
            <AlertTriangle size={32} color={palette.red} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 16, fontWeight: 600, color: palette.g800, marginBottom: 6 }}>Something went wrong</div>
            <div style={{ fontSize: 13, color: palette.g500, marginBottom: 16 }}>
              {this.state.error?.message ?? 'An unexpected error occurred.'}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 20px', borderRadius: 8, border: 'none',
                background: palette.red, color: palette.white,
                fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Try Again</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
